import React, { useState, useEffect, useRef } from 'react';
import { IconMessageCircle2, IconX, IconSend, IconRobot, IconUser } from '@tabler/icons-react';
import { initChatSession, sendChatMessage } from '../controllers/chatbot.controller';
import socket from '../utils/socket';
import { API } from '../config/config';

const BASE_URL = API.replace('/api/v1', '');

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [quickQuestions, setQuickQuestions] = useState([]);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Load session from localStorage if exists
    const storedSession = localStorage.getItem('chatbot_session_id');
    if (storedSession) {
      setSessionId(storedSession);
    }
    // Auto open chat after 3 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      handleInitChat();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (sessionId) {
      // Listen for socket events for this session
      const handleNewMessage = (msg) => {
        setMessages(prev => [...prev, msg]);
      };
      
      // We need to join the room on the backend.
      socket.emit('join_chat_session', sessionId);
      socket.on('chat_reply', handleNewMessage);

      return () => {
        socket.off('chat_reply', handleNewMessage);
      };
    }
  }, [sessionId]);

  const handleInitChat = async () => {
    setIsLoading(true);
    try {
      const data = await initChatSession('Visitor', '', sessionId);
      setSessionId(data.sessionId);
      localStorage.setItem('chatbot_session_id', data.sessionId);
      
      if (data.quickQuestions) {
        setQuickQuestions(data.quickQuestions);
      }

      if (data.history && data.history.length > 0) {
        setMessages(data.history);
      } else {
        setMessages([{
          id: Date.now(),
          sender_type: 'bot',
          message_text: data.welcomeMessage || "Welcome to ehospitalitynepal, we are ready to serve your hospitality",
          created_at: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error("Failed to init chat", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e, textOverride = null) => {
    if (e) e.preventDefault();
    const userText = textOverride || inputValue;
    if (!userText.trim() || !sessionId) return;

    setInputValue('');
    
    // Optimistic UI
    setMessages(prev => [...prev, {
      id: Date.now(),
      sender_type: 'user',
      message_text: userText,
      created_at: new Date().toISOString()
    }]);

    setIsLoading(true);
    try {
      const data = await sendChatMessage(sessionId, userText);
      if (data.reply) {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          sender_type: 'bot',
          message_text: typeof data.reply === 'string' ? data.reply : data.reply.message_text,
          answer_image: data.image || (data.reply && data.reply.answer_image),
          created_at: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error("Failed to send message", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999] font-sans">
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center w-16 h-16 rounded-full shadow-2xl text-white transition-transform duration-300 transform hover:scale-110 ${isOpen ? 'rotate-90 bg-slate-800' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}
        style={{ backdropFilter: 'blur(10px)' }}
      >
        {isOpen ? <IconX size={28} /> : <IconMessageCircle2 size={32} />}
      </button>

      {/* Chat Window */}
      <div 
        className={`absolute bottom-20 right-0 w-[380px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col transition-all duration-500 origin-bottom-right ${isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-0 opacity-0 translate-y-10 pointer-events-none'}`}
        style={{ height: '600px', maxHeight: '80vh' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5 text-white flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-bold text-lg tracking-wide">Support Assistant</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
              <p className="text-xs text-blue-100 opacity-90">We typically reply instantly</p>
            </div>
          </div>
          <IconRobot size={36} className="opacity-80" />
        </div>

        {/* Messages */}
        <div className="flex-1 p-4 overflow-y-auto bg-slate-50 space-y-4">
          {messages.map((msg, idx) => {
            const isBot = msg.sender_type === 'bot' || msg.sender_type === 'admin';
            return (
              <div key={idx} className={`flex ${isBot ? 'justify-start' : 'justify-end'} animate-fade-in-up`}>
                <div className={`max-w-[85%] rounded-2xl p-4 shadow-sm relative ${isBot ? 'bg-white text-gray-800 rounded-tl-none border border-gray-100' : 'bg-gradient-to-br from-indigo-500 to-blue-600 text-white rounded-tr-none'}`}>
                  <p className="text-sm leading-relaxed">{msg.message_text}</p>
                  
                  {msg.answer_image && (
                    <div 
                      className="mt-3 rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setFullScreenImage(msg.answer_image.startsWith('http') ? msg.answer_image : `${BASE_URL}/${msg.answer_image}`)}
                    >
                      <img 
                        src={msg.answer_image.startsWith('http') ? msg.answer_image : `${BASE_URL}/${msg.answer_image}`} 
                        alt="Attachment" 
                        className="w-full h-auto"
                      />
                    </div>
                  )}
                  
                  <span className={`text-[10px] mt-2 block ${isBot ? 'text-gray-400' : 'text-blue-100 opacity-80'} text-right`}>
                    {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </span>
                </div>
              </div>
            );
          })}
          
          {isLoading && (
            <div className="flex justify-start animate-pulse">
              <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm border border-gray-100 flex gap-2">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-75"></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-150"></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-gray-100 shrink-0">
          {quickQuestions.length > 0 && (
            <div 
              className="flex items-center gap-2 mb-3 overflow-x-auto pb-1 pt-0.5 no-scrollbar scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onWheel={(e) => {
                if (e.deltaY !== 0) {
                  e.currentTarget.scrollLeft += e.deltaY;
                }
              }}
            >
              {quickQuestions.map(q => (
                <button
                  key={q.id}
                  onClick={() => handleSendMessage(null, q.question)}
                  className="whitespace-nowrap shrink-0 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded-full px-3.5 py-1.5 hover:bg-blue-100 hover:border-blue-300 transition-all font-medium active:scale-95 shadow-sm"
                >
                  {q.question}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your question..."
              className="flex-1 bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-full px-5 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-600 text-white shadow-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <IconSend size={20} className="-ml-0.5" />
            </button>
          </form>
          <div className="text-center mt-3">
            <span className="text-[10px] text-gray-400 font-medium tracking-wider uppercase">Powered by STJ Technologies</span>
          </div>
        </div>
      </div>

      {/* Full Screen Image Modal */}
      {fullScreenImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-[10000] flex items-center justify-center p-4"
          onClick={() => setFullScreenImage(null)}
        >
          <div className="relative max-w-4xl w-full h-full flex items-center justify-center">
            <button 
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-80 transition-all"
              onClick={() => setFullScreenImage(null)}
            >
              <IconX size={24} />
            </button>
            <img 
              src={fullScreenImage} 
              alt="Fullscreen View" 
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out forwards;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
